import { useAppDispatch, useAppSelector } from '../../state/hooks'
import ConfirmDialog, {
  ConfirmDialogProps,
  ConfirmDialogState,
} from '../../components/ConfirmDialog'
import {
  selectSubmitCreativeForApprovalConfirmDialog,
  setSubmitCreativeForApprovalConfirmDialog,
} from '../../state/appSlice'
import { useFragment, useMutation } from 'react-relay'
import { graphql } from 'relay-runtime'
import { toast } from 'react-toastify'
import { SubmitCreativeForApprovalConfirmDialogMutation } from './__generated__/SubmitCreativeForApprovalConfirmDialogMutation.graphql'
import { getMetaAdListQueryVariables } from '../../pages/HomeRoute'
import { SubmitCreativeForApprovalConfirmDialogBrand$key } from './__generated__/SubmitCreativeForApprovalConfirmDialogBrand.graphql'
import {
  useSelectableItemContext,
  useSelectedIdsList,
} from '../../components/SelectableItemList/context'
import { useGlobalState } from '../../state/v2/store'
import { useTypeParam } from '../../state/url/type'

export interface SubmitCreativeForApprovalConfirmDialogProps
  extends ConfirmDialogState {
  brandFragmentRequest: SubmitCreativeForApprovalConfirmDialogBrand$key
  refreshData: () => void
}

export const submitCreativeForApprovalConfirmDialogBrandFragment = graphql`
  fragment SubmitCreativeForApprovalConfirmDialogBrand on Brand {
    id
    invalidDraftMetaAdCount
  }
`

const updateCampaignMutation = graphql`
  mutation SubmitCreativeForApprovalConfirmDialogMutation(
    $input: GetRequest!
    $metaAdsFilter: MetaAdsFilter
    $adIds: [String!]
  ) {
    submitAdsForApproval(
      input: $input
      metaAdsFilter: $metaAdsFilter
      adIds: $adIds
    ) {
      brand {
        id
        draftMetaAdCount
        unapprovedMetaAdCount
        ...CreativeList
        ...HomeAction
      }
      submittedCount
    }
  }
`

const SubmitCreativeForApprovalConfirmDialog = (
  props: SubmitCreativeForApprovalConfirmDialogProps
) => {
  const dispatch = useAppDispatch()
  const selectedIds = useSelectedIdsList()
  const { clearSelection } = useSelectableItemContext()
  const [request, loading] =
    useMutation<SubmitCreativeForApprovalConfirmDialogMutation>(
      updateCampaignMutation
    )
  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))
  const [type] = useTypeParam()

  const confirm = () => {
    const onCompleted = (response: any) => {
      if (response.submitAdsForApproval.submittedCount !== selectedIds.length) {
        toast.warning('Some unfinished ads were not submitted for approval.')
      } else {
        toast.success(`Successfully submitted ads for approval.`)
      }
      dispatch(setSubmitCreativeForApprovalConfirmDialog({ open: false }))
      clearSelection()
      props.refreshData()
    }
    const onError = (error: Error) => {
      console.log(error)
      toast.error(`Unable to submit ads for approval.`)
    }
    request({
      variables: {
        input: {
          id: brandId!,
        },
        adIds: selectedIds,
        ...getMetaAdListQueryVariables(type),
      },
      onCompleted,
      onError,
    })
  }

  let dialogProps = { open: props.open } as ConfirmDialogProps

  return (
    <>
      <ConfirmDialog
        {...dialogProps}
        confirm={confirm}
        close={() =>
          dispatch(
            setSubmitCreativeForApprovalConfirmDialog({
              open: false,
            })
          )
        }
        disableActions={loading}
        body="Would you like to submit ads for approval? An email will be sent to the approvers to notify them that they have ads to approve."
        confirmText="Submit"
        title="Submit Ads for Approval?"
      />
    </>
  )
}

export default SubmitCreativeForApprovalConfirmDialog

export const SubmitCreativeForApprovalConfirmDialogContainer = (
  props: SubmitCreativeForApprovalConfirmDialogProps
) => {
  const data = useAppSelector(selectSubmitCreativeForApprovalConfirmDialog)
  if (!data.open) {
    return <></>
  }
  return (
    <SubmitCreativeForApprovalConfirmDialog
      brandFragmentRequest={props.brandFragmentRequest}
      refreshData={props.refreshData}
      {...data}
    />
  )
}
